import React from "react";
import { Box, Skeleton, Typography } from "@mui/material";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

import InfoTooltip from "../../components/InfoTooltip";

// Shared by the tile and its skeleton so the ghost tile has the same footprint —
// the same reason the desktop `AnalyticsBriefSkeleton` reuses `classes.container`.
const TILE_SX = {
  flex: "1 0 auto",
  minWidth: 104,
  bgcolor: "primary.clr-50",
  borderRadius: 3,
  px: 3,
  py: 3,
};

/**
 * Loading placeholder for a stat tile. Matches the desktop
 * `AnalyticsBriefSkeleton`, which skeletons the label as well as the value.
 */
function StatCardSkeleton() {
  return (
    <Box sx={TILE_SX}>
      <Box sx={{ mb: 2 }}>
        <Skeleton variant="rectangular" width="80%" height={14} />
      </Box>
      <Skeleton variant="rectangular" width="60%" height={24} />
    </Box>
  );
}

/**
 * Desktop shows two separate tooltips per tile: the info icon beside the title
 * explains what the number is (`AnalyticsBrief` -> `subTitle`), and the trend
 * badge explains the comparison (`info`). There's only room for one tap target
 * on a phone tile, so both sentences share the icon — the trend half only when
 * a trend is actually on screen.
 */
function tooltipFor(stat) {
  return [stat.info, stat.delta ? stat.deltaInfo : null]
    .filter(Boolean)
    .join(" ");
}

/**
 * The three stat tiles under the welcome header (Rank / Your Average /
 * Batch Average). Horizontally scrollable so they never overflow narrow phones.
 *
 * `delta` (optional) is `{ value: '3%', up: boolean }`. `info` / `deltaInfo`
 * (optional) are the tooltip copy, shown on tapping the tile's info icon.
 */
export default function StatCards({ stats = [], loading = false }) {
  return (
    <Box
      sx={{
        display: "flex",
        gap: 3,
        overflowX: "auto",
        pb: 1,
        "&::-webkit-scrollbar": { display: "none" },
      }}
    >
      {loading
        ? Array.from(new Array(3)).map((_, i) => <StatCardSkeleton key={i} />)
        : stats.map((stat) => (
            <Box key={stat.key} sx={TILE_SX}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                {stat.key === "rank" && (
                  <EmojiEventsOutlinedIcon
                    sx={{ fontSize: 14, color: "neutral.clr-500" }}
                  />
                )}
                <Typography variant="body02-regular" color="neutral.clr-600">
                  {stat.label}
                </Typography>
                <InfoTooltip title={tooltipFor(stat)} />
              </Box>

              <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
                <Typography variant="h5-bold" color="neutral.clr-900">
                  {stat.value}
                </Typography>
                {stat.delta && (
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    {stat.delta.up ? (
                      <ArrowDropUpIcon
                        sx={{ fontSize: 18, color: "success.clr-500" }}
                      />
                    ) : (
                      <ArrowDropDownIcon
                        sx={{ fontSize: 18, color: "danger.clr-500" }}
                      />
                    )}
                    <Typography
                      variant="body02-semiBold"
                      color={stat.delta.up ? "success.clr-500" : "danger.clr-500"}
                    >
                      {stat.delta.value}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          ))}
    </Box>
  );
}
